import { useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { exportVaultBundle } from '@/lib/librarian/exportVault';
import { ingestSourceDocument } from '@/lib/librarian/ingestSource';
import { initializeVault } from '@/lib/librarian/initializeVault';
import { lintVault } from '@/lib/librarian/lintVault';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import type { LibrarianIngestResult, LibrarianLintResult } from '@/lib/librarian/types';
import { toast } from '@/components/ui/use-toast';

export function useLibrarianOperations(knowledgeBaseId?: string | null, onRefresh?: () => Promise<void> | void) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lastIngestResult, setLastIngestResult] = useState<LibrarianIngestResult | null>(null);
  const [lastLintResult, setLastLintResult] = useState<LibrarianLintResult | null>(null);

  const refresh = useCallback(async () => {
    await onRefresh?.();
  }, [onRefresh]);

  const initialize = useCallback(async () => {
    if (!knowledgeBaseId) return null;

    setLoading(true);
    try {
      const vault = await initializeVault(knowledgeBaseId);
      await refresh();
      return vault;
    } catch (error) {
      console.error('Failed to initialize vault:', error);
      toast({ title: 'Error', description: 'Failed to initialize the Librarian vault.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId, refresh]);

  const ingestDocument = useCallback(async (document: KnowledgeBaseDocument) => {
    setLoading(true);
    try {
      const result = await ingestSourceDocument(document.id, user?.id);
      setLastIngestResult(result);
      await refresh();
      return result;
    } catch (error) {
      console.error('Failed to ingest source document:', error);
      toast({ title: 'Error', description: 'Failed to process this source with Librarian.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [refresh, user?.id]);

  const runLint = useCallback(async () => {
    if (!knowledgeBaseId) return null;

    setLoading(true);
    try {
      const result = await lintVault(knowledgeBaseId, user?.id);
      setLastLintResult(result);
      await refresh();
      return result;
    } catch (error) {
      console.error('Failed to lint vault:', error);
      toast({ title: 'Error', description: 'Failed to lint the Librarian vault.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId, refresh, user?.id]);

  const exportVault = useCallback(async () => {
    if (!knowledgeBaseId) return null;

    setLoading(true);
    try {
      const bundle = await exportVaultBundle(knowledgeBaseId);
      const blob = new Blob([bundle.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = bundle.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      return bundle;
    } catch (error) {
      console.error('Failed to export vault:', error);
      toast({ title: 'Error', description: 'Failed to export the Librarian vault.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  return {
    loading,
    lastIngestResult,
    lastLintResult,
    initialize,
    ingestDocument,
    runLint,
    exportVault,
  };
}